// CampaignPage.js
import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import CampaignDrawer from "../components/CampaignDrawer";
import { getAllCampaigns, deleteCampaign } from "../lib/api";

const CampaignPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await getAllCampaigns(token);
      if (response.status === "success") {
        setCampaigns(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const openEditDrawer = (campaign) => {
    setSelectedCampaign(campaign);
    setShowEditDrawer(true);
  };

  const handleDelete = async (campaignId) => {
    if (window.confirm("Are you sure you want to delete this campaign?")) {
      try {
        const token = localStorage.getItem("token");
        const response = await deleteCampaign(campaignId, token);
        if (response.status === "success") {
          fetchCampaigns();
        }
      } catch (error) {
        console.error("Error deleting campaign:", error);
      }
    }
  };

  const handleDrawerSuccess = () => {
    fetchCampaigns();
  };

  const toggleDescription = (campaignId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [campaignId]: !prev[campaignId]
    }));
  };

  const paged = campaigns.slice((page - 1) * perPage, page * perPage);
  const pageCount = Math.ceil(campaigns.length / perPage) || 1;

  // Function to truncate text and add read more
  const renderDescription = (description, campaignId) => {
    if (!description) return "-";
    
    const isExpanded = expandedDescriptions[campaignId] || false;
    
    if (description.length <= 100) {
      return <div className="description-cell">{description}</div>;
    }
    
    return (
      <div className="description-cell">
        <div className={`description-text ${!isExpanded ? 'collapsed' : ''}`}>
          {description}
        </div>
        <button
          type="button"
          className="read-more-btn"
          onClick={(e) => {
            e.stopPropagation();
            toggleDescription(campaignId);
          }}
        >
          {isExpanded ? "Read less" : "Read more"}
        </button>
      </div>
    );
  };

  return (
    <>
      <Breadcrumb title="Campaigns" />

      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[5, 10, 20, 50].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>

            <form
              className="navbar-search"
              onSubmit={(ev) => {
                ev.preventDefault();
                const searchTerm = ev.target.search.value.toLowerCase();
                if (searchTerm) {
                  const filtered = campaigns.filter(c => 
                    c.campaign.Name.toLowerCase().includes(searchTerm)
                  );
                  setCampaigns(filtered);
                } else {
                  fetchCampaigns();
                }
              }}
            >
              <input type="text" className="bg-base h-40-px w-auto" name="search" placeholder="Search" />
              <Icon icon="ion:search-outline" className="icon" />
            </form>
          </div>

          <button
            type="button"
            className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            onClick={() => setShowAddDrawer(true)}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add Campaign
          </button>
        </div>

        <div className="card h-100 p-16 radius-12">
          <div className="table-responsive">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col" className="col-name">Name</th>
                  <th scope="col" className="col-description">Description</th>
                  <th scope="col" className="col-status">Status</th>
                  <th scope="col" className="col-action">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="12" className="text-center">Loading campaigns...</td></tr>
                ) : paged.length ? (
                  paged.map((item, idx) => (
                    <tr key={item.campaign.ID}>
                      <td className="campaign-name">{item.campaign.Name}</td>
                      <td className="description-cell-wrapper">
                        {renderDescription(item.campaign.Description, item.campaign.ID)}
                      </td>
                      <td>
                        <span className={`badge ${item.campaign.IsActive ? 'bg-success' : 'bg-danger'}`}>
                          {item.campaign.IsActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-10">
                          <button
                            type="button"
                            className="bg-success-100 text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            onClick={() => openEditDrawer(item)}
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                          <button
                            type="button"
                            className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            onClick={() => handleDelete(item.campaign.ID)}
                          >
                            <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="12" className="text-center">No campaigns found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, campaigns.length)} of {campaigns.length} entries
            </span>
            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
              <li className="page-item">
                <Link
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                  to="#"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <Icon icon="ep:d-arrow-left" />
                </Link>
              </li>
              {Array.from({ length: pageCount }).map((_, i) => (
                <li key={i} className="page-item">
                  <Link
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${page === i + 1 ? "bg-primary-600 text-white" : "bg-neutral-200"}`}
                    to="#"
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </Link>
                </li>
              ))}
              <li className="page-item">
                <Link
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                  to="#"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  <Icon icon="ep:d-arrow-right" />
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <CampaignDrawer
        isOpen={showAddDrawer}
        onClose={() => setShowAddDrawer(false)}
        onSuccess={handleDrawerSuccess}
      />

      <CampaignDrawer
        isOpen={showEditDrawer}
        onClose={() => {
          setShowEditDrawer(false);
          setSelectedCampaign(null);
        }}
        campaignToEdit={selectedCampaign}
        onSuccess={handleDrawerSuccess}
      />
    </>
  );
};

export default CampaignPage;